import { useState } from 'react';

import type { ActivityChange } from '@repo/shared-types';

/**
 * Renders the before/after of one change.
 *
 * The whole feature exists so somebody can answer "what actually changed?"
 * without diffing two JSON blobs by eye, so values are shown side by side per
 * field, with long text (prompts, mostly) collapsed until asked for.
 */

const COLLAPSE_OVER = 240;

/** JSON for structures, plain text for scalars — quotes around every string
 * make a prompt diff unreadable. */
const render = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value === '' ? '(empty)' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const isStructured = (value: unknown) => value !== null && typeof value === 'object';

const ValueBlock = ({
  value,
  tone,
  expanded,
}: {
  value: unknown;
  tone: 'before' | 'after';
  expanded: boolean;
}) => {
  const text = render(value);
  const shown =
    expanded || text.length <= COLLAPSE_OVER ? text : `${text.slice(0, COLLAPSE_OVER)}…`;

  const toneClass =
    tone === 'before'
      ? 'bg-red-500/5 border-red-500/20 text-red-900 dark:text-red-200'
      : 'bg-emerald-500/5 border-emerald-500/20 text-emerald-900 dark:text-emerald-200';

  return (
    <div className={`min-w-0 flex-1 rounded-md border px-3 py-2 ${toneClass}`}>
      <div className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wide uppercase">
        {tone === 'before' ? 'Before' : 'After'}
      </div>
      <pre
        className={`overflow-x-auto text-xs leading-relaxed ${
          isStructured(value) ? 'font-mono' : 'font-sans break-words whitespace-pre-wrap'
        }`}
      >
        {shown}
      </pre>
    </div>
  );
};

export const ActivityChangeRow = ({ change }: { change: ActivityChange }) => {
  const [expanded, setExpanded] = useState(false);

  const beforeText = render(change.before);
  const afterText = render(change.after);
  const isLong = beforeText.length > COLLAPSE_OVER || afterText.length > COLLAPSE_OVER;

  return (
    <div className="space-y-1.5 border-t py-3 first:border-t-0 dark:border-gray-700">
      <div className="flex items-center justify-between gap-2">
        <code className="text-xs font-semibold">{change.path}</code>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="text-muted-foreground hover:text-foreground text-[11px] underline"
          >
            {expanded ? 'Collapse' : 'Show full value'}
          </button>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <ValueBlock value={change.before} tone="before" expanded={expanded} />
        <ValueBlock value={change.after} tone="after" expanded={expanded} />
      </div>
    </div>
  );
};

export const ActivityDiff = ({ changes }: { changes: ActivityChange[] | null | undefined }) => {
  const [showAll, setShowAll] = useState(false);

  if (!changes || changes.length === 0) {
    return (
      <p className="text-muted-foreground py-3 text-xs">
        No field-level diff was captured for this entry.
      </p>
    );
  }

  const visible = showAll ? changes : changes.slice(0, 8);

  return (
    <div>
      {visible.map((change) => (
        <ActivityChangeRow key={change.path} change={change} />
      ))}
      {changes.length > visible.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-muted-foreground hover:text-foreground mt-2 text-xs underline"
        >
          Show {changes.length - visible.length} more changed field
          {changes.length - visible.length === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
};
